// ---------- le renard qui parle ----------
// Le renard du logo (public/icons/renard.webp, celui du lancement) devient la voix de l'initiation
// (2026-10-31) : il accueille, annonce les gestes, et revient saluer la première pièce finie. Une
// voix et non plus des panneaux : les mêmes phrases, dites par quelqu'un, se lisent comme une
// invitation plutôt que comme une notice — et c'est la notice qu'un nouveau venu saute.
//
// Ce module ne fait que deux choses : faire apparaître une phrase mot à mot pendant que le renard
// « parle », et le faire surgir au bas de la feuille avec une bulle. Il ne sait pas QUAND parler :
// gestureDialog.ts et piece.ts en décident.

import { noMotion } from './anim';

/** L'image : la même que le lancement, déjà dans le cache quand l'initiation commence. */
export const FOX_SRC = 'icons/renard.webp';

/** L'écart entre deux mots. Mesuré à l'œil en capture : à 40 ms la phrase arrive d'un bloc et
 *  l'effet ne se voit plus ; à 90, une phrase de vingt mots fait attendre deux secondes un joueur
 *  qui lit plus vite qu'elle. */
const WORD_MS = 55;
/** Ce que dure l'entrée d'un mot (cf. @keyframes sayWord). */
const WORD_IN_MS = 320;

/**
 * Fait dire `els`, l'un après l'autre, mot à mot, à partir de `delayMs`. Rend le moment où le
 * dernier mot est arrivé, pour que le renard s'arrête de parler en même temps.
 *
 * Le texte est relu dans l'élément lui-même, que l'i18n a rempli (data-i18n) : la phrase n'est
 * écrite qu'à un endroit, et une langue changée en cours de route est celle qu'on entend. Les mots
 * gardent leurs espaces entre eux, donc textContent ne change pas — un second appel retrouve la
 * même phrase.
 */
export function say(els: readonly HTMLElement[], delayMs = 0): number {
  if (noMotion()) return 0;
  let at = delayMs;
  for (const el of els) {
    const words = (el.textContent ?? '').trim().split(/\s+/);
    el.replaceChildren();
    words.forEach((w, i) => {
      if (i) el.append(' ');
      const s = document.createElement('span');
      s.className = 'say-w';
      s.textContent = w;
      s.style.animationDelay = `${at}ms`;
      el.append(s);
      at += WORD_MS;
    });
    // Un souffle entre deux phrases, comme on en marque un en parlant.
    at += 4 * WORD_MS;
  }
  return at - 4 * WORD_MS + WORD_IN_MS;
}

/** Le renard bouge la tête tant qu'il parle, de `fromMs` à `toMs`. */
export function talk(fox: HTMLElement, fromMs: number, toMs: number): number[] {
  if (noMotion()) return [];
  return [
    window.setTimeout(() => fox.classList.add('talk'), fromMs),
    window.setTimeout(() => fox.classList.remove('talk'), toMs),
  ];
}

/** Combien de temps le renard reste au bas de la feuille une fois sa phrase dite. */
const PEEK_HOLD_MS = 4200;
const PEEK_OUT_MS = 500;

/**
 * Le renard surgit du bas de la feuille et dit `title`, puis `line`. Posé SUR la feuille, donc
 * tout en `pointer-events: none` — sinon il volerait les taps des cases dessous (cf. CLAUDE.md) ;
 * le joueur peut reprendre la grande feuille pendant qu'il parle.
 */
export function foxPeek(title: string, line: string): void {
  const box = document.querySelector<HTMLElement>('.sheetbox');
  if (!box) return;
  const el = document.createElement('div');
  el.className = 'fox-peek';
  const fox = document.createElement('img');
  fox.className = 'fox-peek-img';
  fox.src = FOX_SRC;
  fox.alt = '';
  const bubble = document.createElement('div');
  bubble.className = 'fox-peek-bubble';
  const b = document.createElement('b');
  b.textContent = title;
  const p = document.createElement('span');
  p.textContent = line;
  bubble.append(b, ' ', p);
  el.append(fox, bubble);
  box.appendChild(el);
  // Le titre et la phrase dans la même bulle, dits à la suite : l'entrée du renard d'abord
  // (cf. @keyframes foxRise), les mots ensuite.
  const said = say([b, p], 380);
  talk(fox, 380, said);
  requestAnimationFrame(() => el.classList.add('on'));
  window.setTimeout(
    () => {
      el.classList.remove('on');
      window.setTimeout(() => el.remove(), PEEK_OUT_MS + 50);
    },
    Math.max(said, 1500) + PEEK_HOLD_MS,
  );
}

// ---------- la fin de la pièce d'initiation ----------

export type PieceChoice = 'finish' | 'library';

/** `pressed` : le choix vient d'un des deux boutons, et non de « précédent » — seul un bouton
 *  pressé dit ce que le joueur a choisi (cf. l'entonnoir, onStep dans piece.ts). */
let pieceDoneCb: ((c: PieceChoice, pressed: boolean) => void) | null = null;
let pieceDoneTimers: number[] = [];

function pdEl(id: string): HTMLElement {
  return document.getElementById(id)!;
}

/**
 * Le renard félicite, montre la vitrine, et demande la suite (#pieceDone dans index.html). Dans
 * l'ordre où on le dirait : le bravo et la bulle, puis les trois promesses une à une, puis la
 * question — les boutons sont là dès le début, pour qui lit plus vite que le renard ne parle.
 * `done` est appelé une fois, avec le choix ; « précédent » vaut « terminer ce dessin », le
 * choix qui ne déplace personne.
 */
export function openPieceDone(done: (c: PieceChoice, pressed: boolean) => void): void {
  const dlg = pdEl('pieceDone');
  if (dlg.getAttribute('aria-hidden') === 'false') return;
  pieceDoneCb = done;
  pdEl('pdFinish').onclick = () => closePieceDone('finish', true);
  pdEl('pdLibrary').onclick = () => closePieceDone('library', true);
  const items = [...dlg.querySelectorAll<HTMLElement>('.pd-list li')];
  const ask = dlg.querySelector<HTMLElement>('.pd-ask')!;
  dlg.setAttribute('aria-hidden', 'false');
  pdEl('pdFinish').focus();
  // L'entrée du renard (gdFoxIn) s'est jouée au chargement de la page, sous un dialogue masqué
  // par `visibility` et non retiré du rendu : on la rejoue.
  const wrap = dlg.querySelector<HTMLElement>('.gd-fox')!;
  wrap.style.animation = 'none';
  void wrap.offsetWidth;
  wrap.style.animation = '';
  if (noMotion()) {
    items.forEach((li) => li.classList.add('on'));
    ask.classList.add('on');
    return;
  }
  items.forEach((li) => li.classList.remove('on'));
  ask.classList.remove('on');
  const fox = dlg.querySelector<HTMLElement>('.gd-fox img')!;
  const said = say([...dlg.querySelectorAll<HTMLElement>('.gd-bubble .gd-say')], 260);
  const timers = talk(fox, 260, said);
  // Une promesse tous les 380 ms : assez pour que chacune soit lue comme une chose à part.
  items.forEach((li, i) =>
    timers.push(window.setTimeout(() => li.classList.add('on'), said + 120 + i * 380)),
  );
  const askAt = said + 120 + items.length * 380 + 200;
  timers.push(window.setTimeout(() => ask.classList.add('on'), askAt));
  pieceDoneTimers = timers;
}

/** Renvoie true quand il a refermé quelque chose, ce qu'attend « précédent » d'Android — cf.
 *  closeOverlays() dans ui.ts. */
export function closePieceDone(choice: PieceChoice = 'finish', pressed = false): boolean {
  const dlg = pdEl('pieceDone');
  if (dlg.getAttribute('aria-hidden') !== 'false') return false;
  dlg.setAttribute('aria-hidden', 'true');
  pieceDoneTimers.forEach(clearTimeout);
  pieceDoneTimers = [];
  dlg.querySelector('.gd-fox img')?.classList.remove('talk');
  const cb = pieceDoneCb;
  pieceDoneCb = null;
  cb?.(choice, pressed);
  return true;
}

/** Referme le dialogue SANS rien décider : la feuille dont il parlait n'est plus là (cf.
 *  closePiece). Appeler son choix déplacerait la caméra, ou ouvrirait la bibliothèque, à propos
 *  d'une autre. */
export function dismissPieceDone(): void {
  pieceDoneCb = null;
  closePieceDone();
}
